import * as React from 'react';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';

import Button, { ButtonProps } from '../../atoms/Button';
import Surface from '../../atoms/Surface';
import Overlay from '../../atoms/Overlay';
import DropdownItem from '../../atoms/DropdownItem';

import styles from './DropdownButton.module.scss';

export interface ItemProps extends Record<string, unknown> {
  className?: string;
  children: React.ReactNode;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

export interface Props<T extends string>
  extends Omit<ButtonProps, 'type' | 'children' | 'onSelect'> {
  options: Array<{
    id: T;
    label: string;
  }>;
  label: string;
  icon?: FontAwesomeIconProps['icon'];
  hideChevron?: boolean;
  itemComponent?: React.FunctionComponent<ItemProps>;
  onSelect?: (id: T) => void;
}

export const DropdownButton = <T extends string>({
  className,
  options,
  label,
  variant = 'outline',
  size = 'md',
  icon = undefined,
  hideChevron = false,
  itemComponent: Item = DropdownItem,
  onSelect,
  ...htmlElementProps
}: Props<T>): React.ReactElement => {
  const [show, setShow] = React.useState(false);
  const btnRef = React.useRef<HTMLDivElement | null>(null);

  const handleSelect = React.useCallback(
    (id: T) => {
      if (onSelect) {
        onSelect(id);
      }
      setShow(false);
    },
    [onSelect, setShow],
  );

  return (
    <div className={classNames(styles.wrapper, className)} ref={btnRef}>
      <Button
        className={styles.button}
        variant={variant}
        size={size}
        icon={icon}
        appendIcon={
          // eslint-disable-next-line no-nested-ternary
          !hideChevron ? (show ? faChevronUp : faChevronDown) : undefined
        }
        onClick={() => setShow(!show)}
        {...htmlElementProps}
      >
        {label}
      </Button>

      <Overlay
        show={show}
        placement="bottom-start"
        target={btnRef.current}
        onHide={() => setShow(false)}
      >
        <Surface
          className={styles.popover}
          backgroundColor="grayLight3"
          padding="sm"
        >
          {options.map((option) => (
            <Item
              key={option.id}
              className="dropdown-item"
              onClick={() => handleSelect(option.id)}
            >
              {option.label}
            </Item>
          ))}
        </Surface>
      </Overlay>
    </div>
  );
};

export default DropdownButton;
